import {GoogleMap, Marker, useLoadScript} from '@react-google-maps/api';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {config} from '../../environment';
import {
  Spinner,
  Text,
  TextAlignment,
  TokenTextAppearance,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';
import './styles.css';
import ChargingStationModal from './chargingStationModal';
import {Poi} from './evMap';
import {customMapStyles} from './constants';
import useMediaQuery from '../../hooks/useMediaQuery';

type MapContainerProps = {
  location: google.maps.LatLngLiteral | null;
  markersDetail: Poi[] | null;
  onMarkerClick?: (poi: Poi) => void;
  onCTAClick?: () => void;
};

const MAX_BOUNDARY_RADIUS = 11000; // 11 km, one more than the map boundry of 10

const MapContainer: React.FC<MapContainerProps> = ({
  location,
  markersDetail,
  onMarkerClick,
  onCTAClick,
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [showBoundaryMessage, setShowBoundaryMessage] =
    useState<boolean>(false);
  const [chargingStationDetails, setChargingStationDetails] =
    useState<Poi | null>(null);
  const {isLoaded} = useLoadScript({
    googleMapsApiKey: `${config.GOOGLE_MAPS_CLIENT_API_KEY}`,
    libraries: ['geometry'],
  });
  const [modalMaxHeight, setModalMaxHeight] = useState<number>(0);
  const {isMobile} = useMediaQuery();

  const mapBoundary = useMemo(() => {
    if (!location || !google?.maps?.geometry) {
      return null;
    }

    const spherical = google.maps.geometry.spherical;
    const northEast = spherical.computeOffset(
      spherical.computeOffset(location, MAX_BOUNDARY_RADIUS, 0),
      MAX_BOUNDARY_RADIUS,
      90,
    );
    const southWest = spherical.computeOffset(
      spherical.computeOffset(location, MAX_BOUNDARY_RADIUS, 180),
      MAX_BOUNDARY_RADIUS,
      270,
    );

    const bounds = {
      north: northEast.lat(),
      east: northEast.lng(),
      south: southWest.lat(),
      west: southWest.lng(),
    };

    return bounds;
  }, [location]);

  const onBoundaryChange = () => {
    const bounds = mapRef.current?.getBounds();
    if (google?.maps?.geometry && bounds && mapBoundary) {
      const spherical = google.maps.geometry.spherical;
      const distanceRight = spherical.computeDistanceBetween(
        {lat: 0, lng: bounds?.getNorthEast().lng()},
        {lat: 0, lng: mapBoundary?.east},
      );

      const distanceLeft = spherical.computeDistanceBetween(
        {lat: 0, lng: bounds?.getSouthWest().lng()},
        {lat: 0, lng: mapBoundary?.west},
      );

      const distanceTop = spherical.computeDistanceBetween(
        {lat: bounds.getNorthEast().lat(), lng: 0},
        {lat: mapBoundary.north, lng: 0},
      );

      const distanceBottom = spherical.computeDistanceBetween(
        {lat: bounds.getSouthWest().lat(), lng: 0},
        {lat: mapBoundary.south, lng: 0},
      );
      const isReachingTop = distanceTop < 1;
      const isReachingBottom = distanceBottom < 1;
      const isReachingRight = distanceRight < 1;
      const isReachingLeft = distanceLeft < 1;

      if (
        isReachingTop ||
        isReachingBottom ||
        isReachingRight ||
        isReachingLeft
      ) {
        setShowBoundaryMessage(true);
        setTimeout(() => {
          setShowBoundaryMessage(false);
        }, 2000);
      }
    }
  };

  const closeModal = () => {
    setChargingStationDetails(null);
  };

  const onClickMarker = (marker: Poi) => {
    setChargingStationDetails(marker);
    onMarkerClick?.(marker);
  };

  const calculateModalMaxHeight = () => {
    const mapContainerHeight = mapRef.current?.getDiv().offsetHeight || 0;
    const mobileMargin = 32;
    const desktopMargin = 40;

    setModalMaxHeight(
      isMobile
        ? mapContainerHeight - mobileMargin * 2
        : mapContainerHeight - desktopMargin * 2,
    );
  };

  useEffect(() => {
    if (location) {
      mapRef.current?.setCenter(location);
    }
  }, [location]);

  return location !== null ? (
    <div
      style={{
        width: '100%',
        height: '100%',
        marginTop: '50px',
        position: 'relative',
      }}
    >
      <div
        className="boundary-message-wrapper"
        style={{
          opacity: showBoundaryMessage ? 1 : 0,
        }}
      >
        <Text
          className="boundary-message"
          appearance={TokenTextAppearance.bigcopy200}
          textAlign={TextAlignment.center}
          color={TokenTextColor.inherit}
        >
          Use location search to update the map
        </Text>
      </div>
      {chargingStationDetails && (
        <ChargingStationModal
          chargingStationDetails={chargingStationDetails}
          onClose={closeModal}
          onCTAClick={onCTAClick}
          maxHeight={modalMaxHeight}
        />
      )}
      {!isLoaded ? (
        <div
          style={{
            height: '100%',
            width: '100%',
            backgroundColor: '#f5f5f5',
            backgroundImage: `url(${config.EV_FIT_ASSETS_BASE_URL}ev-fit-map-skeleton-min.png)`,
            position: 'absolute',
            backgroundSize: 'cover',
            left: 0,
            top: 0,
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              backgroundColor: '#f5f5f5',
              opacity: 0.5,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)',
              }}
            >
              <Spinner variant="large" />
            </div>
          </div>
        </div>
      ) : (
        <GoogleMap
          mapContainerClassName="map-container"
          center={location}
          zoom={14}
          onBoundsChanged={onBoundaryChange}
          options={{
            ...(mapBoundary && {
              restriction: {
                latLngBounds: mapBoundary,
                strictBounds: false,
              },
            }),
            maxZoom: 17,
            minZoom: 13,
            disableDefaultUI: true,
            clickableIcons: false,
            styles: customMapStyles,
            gestureHandling: 'cooperative',
          }}
          onLoad={(map) => {
            mapRef.current = map;
            calculateModalMaxHeight();
          }}
        >
          {markersDetail?.map((marker) => {
            const {lat, lon} = marker.location.position;
            const isMarkerSelected = chargingStationDetails?.id === marker.id;
            const icon = isMarkerSelected
              ? `${config.EV_FIT_ASSETS_BASE_URL}ev-pin-selected.svg`
              : `${config.EV_FIT_ASSETS_BASE_URL}ev-pin.svg`;
            return (
              <Marker
                key={marker.id}
                position={{lat: lat, lng: lon}}
                onClick={() => onClickMarker(marker)}
                icon={icon}
              />
            );
          })}
        </GoogleMap>
      )}
    </div>
  ) : null;
};

export default MapContainer;
